/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";
// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import brandWhite from "assets/images/logo-arinet.png";
import brandDark from "assets/images/logo-arinet.png";

import * as session from "helpers/session"

import './App.css';

export default function App() {

    const navigate = useNavigate()
    const [controller, dispatch] = useMaterialUIController();
    const {
        miniSidenav,
        direction,
        layout,
        openConfigurator,
        sidenavColor,
        transparentSidenav,
        whiteSidenav,
        darkMode,
    } = controller;
    const [onMouseEnter, setOnMouseEnter] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [logged, setLogged] = useState(false);


    const [rtlCache, setRtlCache] = useState(null);
    const { pathname } = useLocation();


    // Cache for the rtl
    useMemo(() => {
        const cacheRtl = createCache({
            key: "rtl",
            stylisPlugins: [rtlPlugin],
        });

        setRtlCache(cacheRtl);
    }, []);

    // Open sidenav when mouse enter on mini sidenav
    const handleOnMouseEnter = () => {
        if (miniSidenav && !onMouseEnter) {
            setMiniSidenav(dispatch, false);
            setOnMouseEnter(true);
        }
    };

    // Close sidenav when mouse leave mini sidenav
    const handleOnMouseLeave = () => {
        if (onMouseEnter) {
            setMiniSidenav(dispatch, true);
            setOnMouseEnter(false);
        }
    };

    // Change the openConfigurator state
    const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

    useEffect(() => {
        const checkAuth = async () => {
            if (!global.user) {
                try {
                    await session.loadWorkerGlobal().then((response) => {
                        session.saveWorkerGlobal(response);
                    });
                    
                    const response = await session.autoLogin();
                    if (!response.error) {
                        session.saveLoginData(response);
                        setLogged(true);
                        setLoaded(true);
                        navigate(session.autoLoginSuccess());
                    } else {
                        setLogged(false);
                        setLoaded(true);
                        navigate(session.autoLoginError());
                    }
                } catch (error) {
                    console.log("Errore autenticazione:", error);
                    setLogged(false);
                    setLoaded(true);
                    navigate("/authentication/sign-in");
                }
            } else {
                setLogged(true);
                setLoaded(true);
            }
        };

        checkAuth();

        // Interceptor per controllare risposte 401
        const originalFetch = window.fetch;
        window.fetch = async (...args) => {
            const response = await originalFetch(...args);
            
            // Se riceviamo 401, l'utente non è più autenticato
            if (response.status === 401) {
                console.log("Token scaduto o non valido, reindirizzamento al login");
                global.user = null;
                global.jwt = null;
                localStorage.setItem("jwt", "");
                setLogged(false);
                setLoaded(true); // Assicurati che sia loaded per mostrare il login
                navigate("/authentication/sign-in");
            }
            
            return response;
        };

        // Cleanup function per ripristinare fetch originale
        return () => {
            window.fetch = originalFetch;
        };
    }, [navigate, logged]);


    const getRoutes = (allRoutes) =>
        allRoutes.map((route) => {
            if (route.permission && global.user) {
                if (!route.permission.includes(global.user.ruolo)) {
                    return null
                }
            }

            if (route.collapse) {
                return getRoutes(route.collapse);
            }

            if (route.route) {
                return <Route exact path={route.route} element={route.component} key={route.key} />;
            }

            return null;
        });


    // Mostra nulla durante il caricamento dell'autenticazione
    if (!loaded) {
        return (
            <ThemeProvider theme={darkMode ? themeDark : theme}>
                <CssBaseline />
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100vh',
                    fontSize: '18px' 
                }}>
                    Caricamento...
                </div>
            </ThemeProvider>
        );
    }

    // Se non loggato, mostra solo le route di autenticazione
    if (!logged || !global.user) {
        return (
            <ThemeProvider theme={darkMode ? themeDark : theme}>
                <CssBaseline />
                <Routes>
                    {getRoutes(routes)}
                    <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
                </Routes>
            </ThemeProvider>
        );
    }

    // Se loggato, mostra l'app completa
    return (
        <ThemeProvider theme={darkMode ? themeDark : theme}>
            <CssBaseline />
            <Sidenav
                color={sidenavColor}
                brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
                brandName=""
                routes={routes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
            />
            {layout === "vr" && <Configurator />}
            <Routes>
                {getRoutes(routes)}
                <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
        </ThemeProvider>
    );
}
